import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ButtonRounded } from '../UISystem/Buttons/ButtonRounded';
import { SHADOWS } from '../../styles/UISystem';
import {
  requestPermissionAndSubscribe,
  logPromptDismissed,
} from '../../services/pushNotificationService';
import NotificationPermissionModal from './NotificationPermissionModal';

/**
 * Notification permission pre-prompt — slide-up bottom sheet (LocationPrompt's
 * pattern; no standard sheet component exists yet). Shown before the browser's
 * native prompt.
 *
 * @param {function} onComplete - Called when the flow finishes ('granted'|'denied'|'dismissed')
 */
const NotificationPrompt = ({ onComplete }) => {
  const [showDeniedModal, setShowDeniedModal] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);

  // Body scroll lock while the sheet is open (ui-design-system.md §4.7)
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const handleTurnOn = async () => {
    setIsRequesting(true);
    const result = await requestPermissionAndSubscribe();
    setIsRequesting(false);
    if (result === 'denied') {
      setShowDeniedModal(true);
    } else {
      onComplete(result);
    }
  };

  const handleNotNow = () => {
    logPromptDismissed();
    onComplete('dismissed');
  };

  if (showDeniedModal) {
    return <NotificationPermissionModal onDismiss={() => onComplete('denied')} />;
  }

  return (
    <motion.div
      className="notification-prompt-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      onClick={() => {
        // Backdrop tap = soft dismiss. Ignore while the native request is in flight.
        if (!isRequesting) handleNotNow();
      }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 2000,
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
      }}
    >
      <motion.div
        className="notification-prompt-sheet"
        initial={{ y: 500 }}
        animate={{ y: 0 }}
        exit={{ y: 500 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          backgroundColor: 'white',
          borderTopLeftRadius: '24px',
          borderTopRightRadius: '24px',
          boxShadow: SHADOWS.TIER3,
          padding: '32px 20px calc(40px + env(safe-area-inset-bottom))',
          textAlign: 'center',
        }}
      >
        <h3 style={{ margin: '0 0 8px', color: '#000', fontWeight: 700, fontSize: '18px' }}>
          Get notified
        </h3>

        <p style={{ margin: '0 0 24px', color: '#666', fontSize: '15px', lineHeight: '1.4' }}>
          When you receive messages
        </p>

        <ButtonRounded
          text="Turn on"
          variant="black"
          size="full"
          onClick={handleTurnOn}
          disabled={isRequesting}
        />

        <div style={{ marginTop: '4px' }}>
          <ButtonRounded
            text="Not now"
            variant="blank"
            size="full"
            onClick={handleNotNow}
            disabled={isRequesting}
          />
        </div>
      </motion.div>
    </motion.div>
  );
};

export default NotificationPrompt;
